import { Lock, ArrowUpRight, AlertTriangle } from "lucide-react";
import {
  ORDER_URL,
  OWNERSHIP_NOTICE,
  PAID,
  LOCK_ALL_SCREENS,
  OVERDUE_MESSAGE,
} from "../../config/preview";

// Wraps a page component. When locked, the child is rendered blurred and
// non-interactive, with a centered lock card floating above it.
// When PAID = true, always renders children.
// When LOCK_ALL_SCREENS = true, always locks and shows the overdue variant.
export function LockedScreen({ children, locked = true }) {
  if (PAID) return children;
  const overdue = LOCK_ALL_SCREENS;
  const effectiveLocked = overdue || locked;
  if (!effectiveLocked) return children;

  const title = overdue ? "Payment overdue" : "Locked in preview";
  const body = overdue
    ? "Access to this screen is restricted. Complete your payment within 12 hours to keep your MVP live."
    : "This screen is part of the full MVP. Unlock it by completing your payment.";
  const cta = overdue ? "Complete payment now" : "Unlock full access";

  return (
    <div className="relative min-h-screen" data-testid="locked-screen">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{ filter: "blur(6px)" }}
      >
        {children}
      </div>

      <div className="fixed inset-0 md:left-64 flex items-center justify-center p-6 z-30 pointer-events-none">
        <div
          data-testid="lock-card"
          className="pointer-events-auto max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-xl p-8 text-center"
        >
          <div
            className={`mx-auto h-12 w-12 rounded-sm flex items-center justify-center ${
              overdue
                ? "bg-rose-50 dark:bg-rose-500/15"
                : "bg-slate-100 dark:bg-slate-800"
            }`}
          >
            {overdue ? (
              <AlertTriangle
                size={20}
                strokeWidth={1.75}
                className="text-rose-600 dark:text-rose-400"
              />
            ) : (
              <Lock
                size={20}
                strokeWidth={1.75}
                className="text-slate-700 dark:text-slate-300"
              />
            )}
          </div>

          {overdue && (
            <p
              data-testid="lock-card-overdue-label"
              className="mt-4 inline-flex items-center gap-1 rounded-sm bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold"
            >
              {OVERDUE_MESSAGE}
            </p>
          )}

          <h2
            data-testid="lock-card-title"
            className="font-display text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 mt-3"
          >
            {title}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
            {body}
          </p>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener"
            data-testid="lock-card-cta"
            className={`mt-6 inline-flex items-center gap-1.5 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors text-white ${
              overdue
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            }`}
          >
            {cta} <ArrowUpRight size={14} strokeWidth={2.25} />
          </a>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-6 leading-relaxed">
            {OWNERSHIP_NOTICE}
          </p>
        </div>
      </div>
    </div>
  );
}
